import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import GoogleMap from '../components/GoogleMaps';
import "../styles/ProfileEdit.css";

const ProfileEdit = ({ profileData, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    business_number: "",
    business_name: "",
    phone: "",
    email: "",
    agency_name: "",
    business_type: "",
    address: "",
    description: "",
    social_links: "",
    location_coords: null,
    branding_image: null
  });
  
  const [errors, setErrors] = useState({});
  const [showMap, setShowMap] = useState(false);
  const [selectedCoords, setSelectedCoords] = useState(null);
  const [mapSearch, setMapSearch] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef(null);

  // API endpoints
  const API_ENDPOINTS = {
    PROFILE_UPDATE: '/api/tour-operators/profile',
    REVERSE_GEOCODE: '/api/maps/reverse-geocode'
  };

  // Memoized business types
  const businessTypes = useMemo(() => [
    "Tour Operator", "Travel Agency", "Adventure Company", "Luxury Travel",
    "Budget Travel", "Cultural Tours", "Adventure Tours", "Wildlife Tours", 
    "Pilgrimage Tours", "Corporate Travel", "Other"
  ], []);

  // Memoized form configuration
  const formConfig = useMemo(() => ({
    business_number: { required: true, placeholder: "Enter your business registration number" },
    business_name: { required: true, placeholder: "Enter your business name" },
    phone: { required: true, placeholder: "Enter your phone number" },
    email: { required: true, placeholder: "Enter your email address" },
    agency_name: { required: true, placeholder: "Enter your agency name" },
    business_type: { required: true },
    address: { required: true, placeholder: "Enter your business address" },
    description: { required: false, placeholder: "Describe your business..." },
    social_links: { required: false, placeholder: "Enter social media links separated by commas" }
  }), []);

  // Memoized form initialization
  const initializeFormData = useCallback((data) => {
    const businessProfile = data.businessProfile || {};
    const initialCoords = businessProfile.location_coords || null;
    
    if (initialCoords) {
      setSelectedCoords({
        lat: initialCoords.latitude || initialCoords.lat,
        lng: initialCoords.longitude || initialCoords.lng
      });
    }

    if (businessProfile.branding_image) {
      setImagePreview(businessProfile.branding_image);
    }

    setFormData({
      business_number: data.business_number || "",
      business_name: data.business_name || "",
      phone: data.phone || "",
      email: data.email || "",
      agency_name: businessProfile.agency_name || "",
      business_type: businessProfile.business_type || "",
      address: businessProfile.address || "",
      description: businessProfile.description || "",
      social_links: Array.isArray(businessProfile.social_links) 
        ? businessProfile.social_links.join(", ")
        : businessProfile.social_links || "",
      location_coords: initialCoords,
      branding_image: businessProfile.branding_image || null
    });
  }, []);

  useEffect(() => {
    if (profileData) {
      initializeFormData(profileData);
    }
  }, [profileData, initializeFormData]);

  // Memoized map location selection handler
  const handleMapLocationSelect = useCallback(async (coords) => {
    if (selectedCoords && selectedCoords.lat === coords.lat && selectedCoords.lng === coords.lng) {
      return;
    }

    setSelectedCoords(coords);
    
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.REVERSE_GEOCODE}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({ lat: coords.lat, lng: coords.lng })
        }
      );

      const result = await response.json();
      const address = result.success && result.results.length > 0 
        ? result.results[0].formatted_address 
        : `Lat: ${coords.lat.toFixed(6)}, Lng: ${coords.lng.toFixed(6)}`;

      setFormData(prev => ({
        ...prev,
        address,
        location_coords: { latitude: coords.lat, longitude: coords.lng }
      }));
    } catch (error) {
      setFormData(prev => ({
        ...prev,
        address: `Lat: ${coords.lat.toFixed(6)}, Lng: ${coords.lng.toFixed(6)}`,
        location_coords: { latitude: coords.lat, longitude: coords.lng }
      }));
    }
  }, [selectedCoords]);

  // Memoized input change handler
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  }, [errors]);

  // Memoized image upload handler
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, branding_image: "Please select a valid image file" }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, branding_image: "Image size should be less than 5MB" }));
      return;
    }

    setImageLoading(true);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Image = event.target.result;
      setImagePreview(base64Image);
      setFormData(prev => ({ ...prev, branding_image: base64Image }));
      setErrors(prev => ({ ...prev, branding_image: "" }));
      setImageLoading(false);
    };
    
    reader.onerror = () => {
      setErrors(prev => ({ ...prev, branding_image: "Failed to read image file" }));
      setImageLoading(false);
    };
    
    reader.readAsDataURL(file);
  }, []);

  // Memoized image removal handler
  const handleRemoveImage = useCallback(() => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, branding_image: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleImageClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Memoized form validation
  const validateForm = useCallback(() => {
    const newErrors = {};

    Object.keys(formConfig).forEach(key => {
      const field = formConfig[key];
      if (field.required && !formData[key]?.trim()) {
        newErrors[key] = `${key.replace('_', ' ')} is required`;
      }
    });

    if (formData.email?.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, formConfig]);

  // Memoized form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSaveLoading(true);

    try { 
      const submitData = {
        business_number: formData.business_number,
        business_name: formData.business_name,
        phone: formData.phone,
        email: formData.email,
        agency_name: formData.agency_name,
        business_type: formData.business_type,
        address: formData.address,
        description: formData.description,
        social_links: formData.social_links,
        location_coords: formData.location_coords,
        branding_image: formData.branding_image
      };

      const authToken = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${API_ENDPOINTS.PROFILE_UPDATE}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(submitData)
        }
      );

      const result = await response.json();

      if (result.success) {
        onSave?.(result);
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      setErrors({ submit: 'Failed to update profile. Please try again.' });
    } finally {
      setSaveLoading(false);
    }
  }, [formData, validateForm, onSave]);

  // Memoized map handlers
  const openLocationPicker = useCallback(() => {
    setShowMap(true);
    setMapSearch(formData.address || "");
  }, [formData.address]);

  const confirmLocationSelection = useCallback(() => {
    setShowMap(false);
  }, []);

  // Memoized basic information fields
  const basicInfoFields = useMemo(() => [
    { id: 'business_number', label: 'Business Number *' },
    { id: 'business_name', label: 'Business Name *' },
    { id: 'phone', label: 'Phone Number *' },
    { id: 'email', label: 'Email Address *' }
  ], []);

  // Memoized business profile fields
  const businessProfileFields = useMemo(() => [
    { id: 'agency_name', label: 'Agency Name *' },
    { id: 'business_type', label: 'Business Type *', type: 'select' },
    { id: 'address', label: 'Business Address *', fullWidth: true },
    { id: 'description', label: 'Business Description', fullWidth: true, type: 'textarea' },
    { id: 'social_links', label: 'Social Media Links', fullWidth: true }
  ], []);

  // Loading state
  if (loading) {
    return (
      <div className="profile-edit-loading">
        <div className="spinner"></div>
        <p>Loading profile data...</p>
      </div>
    );
  }

  return (
    <div className="profile-edit-container">
      <div className="profile-edit-header">
        <h2>Edit Profile</h2>
        <p>Update your business information and profile details</p>
      </div>

      <form onSubmit={handleSubmit} className="profile-edit-form">
        {/* Basic Information Section */}
        <div className="form-section">
          <h3>Basic Information</h3>
          <div className="form-grid">
            {basicInfoFields.map(field => (
              <div key={field.id} className="form-group">
                <label htmlFor={field.id}>{field.label}</label>
                <input
                  type={formConfig[field.id]?.type || "text"}
                  id={field.id}
                  name={field.id}
                  value={formData[field.id]}
                  onChange={handleInputChange}
                  className={errors[field.id] ? "error" : ""}
                  placeholder={formConfig[field.id]?.placeholder}
                />
                {errors[field.id] && (
                  <span className="error-message">{errors[field.id]}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Branding Image Section */}
        <div className="form-section">
          <h3>Branding Image</h3>
          <div className="branding-image-section">
            <div className="image-upload-area">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              
              {imagePreview ? (
                <div className="image-preview-container">
                  <img 
                    src={imagePreview} 
                    alt="Branding preview" 
                    className="branding-image-preview"
                    loading="lazy"
                  />
                  <div className="image-actions">
                    <button 
                      type="button" 
                      className="btn-change-image"
                      onClick={handleImageClick}
                    >
                      Change Image
                    </button>
                    <button 
                      type="button" 
                      className="btn-remove-image"
                      onClick={handleRemoveImage}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  className="image-upload-placeholder"
                  onClick={handleImageClick}
                >
                  {imageLoading ? (
                    <div className="image-loading">
                      <div className="spinner-small"></div>
                      <p>Uploading image...</p>
                    </div>
                  ) : (
                    <>
                      <div className="upload-icon">📷</div>
                      <p>Click to upload branding image</p>
                      <small>Recommended: 800x400px, JPG/PNG, max 5MB</small>
                    </>
                  )}
                </div>
              )}
              
              {errors.branding_image && (
                <span className="error-message">{errors.branding_image}</span>
              )}
            </div>
          </div>
        </div>

        {/* Business Profile Section */}
        <div className="form-section">
          <h3>Business Profile</h3>
          <div className="form-grid">
            {businessProfileFields.map(field => (
              <div key={field.id} className={`form-group ${field.fullWidth ? 'full-width' : ''}`}>
                <label htmlFor={field.id}>{field.label}</label>
                
                {field.type === 'select' ? (
                  <select
                    id={field.id}
                    name={field.id}
                    value={formData[field.id]}
                    onChange={handleInputChange}
                    className={errors[field.id] ? "error" : ""}
                  >
                    <option value="">Select Business Type</option>
                    {businessTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    id={field.id}
                    name={field.id}
                    value={formData[field.id]}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder={formConfig[field.id]?.placeholder}
                  />
                ) : field.id === 'address' ? (
                  <div className="address-input-group">
                    <input
                      type="text"
                      id={field.id}
                      name={field.id}
                      value={formData[field.id]}
                      onChange={handleInputChange}
                      className={errors[field.id] ? "error" : ""}
                      placeholder={formConfig[field.id]?.placeholder}
                      readOnly
                    />
                    <button 
                      type="button" 
                      className="location-picker-btn"
                      onClick={openLocationPicker}
                    >
                      🗺️
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    id={field.id}
                    name={field.id}
                    value={formData[field.id]}
                    onChange={handleInputChange}
                    className={errors[field.id] ? "error" : ""}
                    placeholder={formConfig[field.id]?.placeholder}
                  />
                )}
                
                {errors[field.id] && (
                  <span className="error-message">{errors[field.id]}</span>
                )}
                
                {field.id === 'address' && (
                  <small className="hint">
                    Click the map icon to select your business location
                  </small>
                )}
                
                {field.id === 'description' && (
                  <small className="hint">
                    Tell customers about your business and what makes you unique
                  </small>
                )}
                
                {field.id === 'social_links' && (
                  <small className="hint">
                    Example: https://facebook.com/yourpage, https://instagram.com/yourprofile
                  </small>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Location Preview */}
        {formData.address && (
          <div className="form-section">
            <h3>Location Preview</h3>
            <div className="location-preview">
              <div className="map-placeholder">
                <p>📍 {formData.address}</p>
                {formData.location_coords && (
                  <small>
                    Coordinates: {formData.location_coords.latitude?.toFixed(6)}, {formData.location_coords.longitude?.toFixed(6)}
                  </small>
                )}
              </div>
              <small className="hint">
                This is how your location will appear to customers
              </small>
            </div>
          </div>
        )}

        {/* Submit Error */}
        {errors.submit && (
          <div className="error-banner">
            {errors.submit}
          </div>
        )}

        {/* Map Modal - Only render when shown */}
        {showMap && (
          <div className="map-modal-overlay">
            <div className="map-modal">
              <div className="map-modal-header">
                <h3>Select Business Location</h3>
                <button 
                  type="button"
                  className="close-map-btn" 
                  onClick={() => setShowMap(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="map-search-container">
                <input
                  type="text"
                  placeholder="Search for a location..."
                  className="map-search-input"
                  value={mapSearch}
                  onChange={(e) => setMapSearch(e.target.value)}
                />
              </div>
              
              <div className="real-map-container">
                <GoogleMap 
                  onLocationSelect={handleMapLocationSelect}
                  selectedCoords={selectedCoords}
                  searchLocation={mapSearch}
                />
              </div>
              
              <div className="map-modal-actions">
                <button 
                  type="button"
                  onClick={confirmLocationSelection}
                  className="confirm-location-btn"
                >
                  Confirm Location
                </button>
                <button 
                  type="button"
                  onClick={() => setShowMap(false)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button 
            type="button" 
            className="btn-cancel"
            onClick={onCancel}
            disabled={saveLoading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-save"
            disabled={saveLoading}
          >
            {saveLoading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default React.memo(ProfileEdit);